/**
 * Autocomplete/Typeahead.
 * exports a function that initializes and returns an autocomplete object.
 * @deprecated Prefer the class in tools/SuiAutocomplete.js
 */

(function(root) {

    const autocomplete = {
        /**
         * @prop {HTMLElement} input Text input.
         */
        input: null,
        /**
         * @prop {HTMLElement} list Autocomplete list.
         */
        list: null,
        /**
         * @prop {String} listLabel Aria description for the list.
         */
        listLabel: 'Suggested results',
        /**
         * @prop {HTMLElement} instructions Screen reader instructions.
         */
        instructions: null,
        /**
         * @prop {Function} throttle Timeout function for throttling requests
         */
        throttle: null,
        /**
         * @prop {Object} xhrSearch Current search request object.
         */
        xhrSearch: null,
        /**
         * @prop {String} queryName Name of the query param from this.input['name']
         */
        queryName: '',
        /**
         * @prop {Function} queryProcessor Escapes or otherwise transforms typed data for query.
         */
        queryProcessor: null,
        /**
         * @prop {Function} dataProcessor Transforms returned data.
         */
        dataProcessor: null,
        /**
         * @prop {Function} selectCallback When an option is selected, this will run with the selected option as its arg.
         */
        selectCallback: null,
        /**
         * @prop {String} typedQuery The query as typed.
         */
        typedQuery: '',
        /**
         * @prop {Number} resultLimit Number of items to show.
         */
        resultLimit: 10,
        /**
         * @prop {Object} queryData Data to send with the query. Could be strings OR HTML input to retrieve values from.
         */
        queryData: {},
        /**
         * @prop {Array} staticData Data to use instead of querying a url.
         */
        staticData: [],
        /**
         * @prop {Boolean} preventSubmit Should we prevent form submission on "enter" in input.
         */
        preventSubmit: false,
        /**
         * @prop {String} instructionsText Text for screen reader instructions.
         */
        instructionsText: '{{count}} options available. Use up and down arrows to browse available options and enter to select one.',
        /**
         * @prop {Boolean} showAllOnFocus When focused the autocomplete will show the list for "all" (i.e. a blank search)
         */
        showAllOnFocus: false,
        /**
         * @prop {Boolean} allowEmptySearch Is a blank/empty search allowed (i.e. show all).
         */
        allowEmptySearch: false,
        /**
         * Select the next list item.
         */
        selectNext: function() {
            const current = this.list.querySelector('li[aria-selected=true]');
            let selected = null;
            if (current === null) {
                selected = this.list.querySelector('li:first-of-type');
                if (selected === null) {
                    return;
                }
            } else {
                selected = current.nextElementSibling;
                if (selected === null) {
                    return;
                }
                current.setAttribute('aria-selected', false);
            }

            selected.setAttribute('aria-selected', true);
            this.input.value = selected.textContent;
        },
        /**
         * Select the previous list item
         */
        selectPrevious: function() {
            const current = this.list.querySelector('li[aria-selected=true]');
            if (current === null) {
                return;
            }
            const selected = current.previousElementSibling;
            if (selected === null) {
                this.input.value = this.typedQuery;
            } else {
                selected.setAttribute('aria-selected', true);
                this.input.value = selected.textContent;
            }
            current.setAttribute('aria-selected', false);
        },
        /**
         * Select an item via clicking on it.
         * @param {HTMLElement} selected Option in list.
         */
        selectByClick: function(selected) {
            const prevSelected = this.list.querySelector('li[aria-selected=true]');
            if (prevSelected !== null) {
                prevSelected.setAttribute('aria-selected', false);
            }
            selected.setAttribute('aria-selected', true);
            this.selectOption();
            if (!this.showAllOnFocus) {
                this.input.focus();
            }
        },
        /**
         * Escape query for use in regex.
         * Taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
         * Should be in some kind of utility module probably, but there is not one of those at this time.
         * @param {String} string String to escape.
         * @return {String}
         */
        escapeRegExp: function(string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
        },
        /**
         * Format data object into HTMLElement
         * @param {Object} object Data object.
         * @return {HTMLElement|null}
         */
        formatOption: function(object) {
            let display = '';
            if (object.display) {
                display = object.display;
            } else {
                const regex = new RegExp(`(${this.escapeRegExp(this.typedQuery)})`, 'ig');
                display = object.label.replace(regex, '<strong>$1</strong>');
            }
            if (!display) {
                return null;
            }

            const option = document.createElement('li');
            option.setAttribute('aria-selected', false);
            option.setAttribute('role', 'option');
            Object.keys(object).forEach((prop) => {
                if (prop === 'label' || prop === 'display') {
                    return;
                }
                option.dataset[prop] = object[prop];
            });
            option.innerHTML = display;
            return option;
        },
        /**
         * Display results.
         * @param {Array} data Array of data objects.
         */
        displayResults: function(data) {
            if (data.length === 0) {
                this.closeList();
                return;
            }

            let options = data.map(this.formatOption, this);
            if (options.length > this.resultLimit && !this.allowEmptySearch) {
                options = options.slice(0, this.resultLimit);
            }
            this.list.innerHTML = '';

            const fragment = document.createDocumentFragment();
            options.forEach((element) => {
                if (!element) {
                    return;
                }
                fragment.appendChild(element);
            });

            this.list.appendChild(fragment);
            this.updateInstructions(options.length);
            this.openList();
        },
        /**
         * Is the list open
         * @return {Boolean}
         */
        isListOpen: function() {
            return !this.list.classList.contains('hidden');
        },
        /**
         * Is one of the options selected.
         * @return {Boolean}
         */
        isOptionSelected: function() {
            return this.list.querySelector('li[aria-selected=true]') !== null;
        },
        /**
         * Open the list.
         */
        openList: function() {
            this.list.style.left = `${this.input.offsetLeft}px`;
            this.list.style.top = `${this.input.offsetTop + this.input.offsetHeight}px`;
            this.list.style.minWidth = `${this.input.offsetWidth}px`;
            this.list.classList.remove('hidden');
            this.input.setAttribute('aria-expanded', true);
        },
        /**
         * Close the list
         */
        closeList: function() {
            this.list.classList.add('hidden');
            this.list.innerHTML = '';
            this.input.setAttribute('aria-expanded', false);
            this.updateInstructions(0);
        },
        /**
         * Select one of the list options.
         */
        selectOption: function() {
            const option = this.list.querySelector('li[aria-selected=true]');
            this.input.dataset.oldvalue = this.input.value;
            this.input.value = option.textContent;
            if (this.selectCallback !== null) {
                this.selectCallback(option);
            }
            this.closeList();
        },
        /**
         * Process the results if necessary.
         * Should have:
         * label text value of the item
         * display (optional) display html for the list
         * @param {Object|Array} data Data from the server.
         * @return {Array}
         */
        processData: function(data) {
            if (this.dataProcessor !== null) {
                return this.dataProcessor(data);
            }
            return data;
        },
        /**
         * Build a query string.
         * @return {String}
         */
        buildQueryString: function() {
            const rawTyped = this.input.value;
            const query = (this.queryProcessor !== null) ? this.queryProcessor(rawTyped) : rawTyped;
            const params = new URLSearchParams();
            params.append(this.queryName, query);
            params.append('limit', this.resultLimit);

            const props = Object.keys(this.queryData);
            props.forEach(function(prop) {
                const value = this.queryData[prop];
                if (typeof value === 'function') {
                    // dynamic param
                    params.append(prop, value());
                    return;
                }
                params.append(prop, value);
            }, this);
            return `?${params.toString()}`;
        },
        /**
         * On load for ajax request.
         */
        xhrOnLoad: function() {
            if (this.xhrSearch.status >= 200 && this.xhrSearch.status < 400) {
                // Success!
                const data = JSON.parse(this.xhrSearch.responseText);
                const formattedData = this.processData(data);
                this.displayResults(formattedData);
            } else {
                // We reached our target server, but it returned an error
                console.log(this.xhrSearch.statusText);
            }
        },
        /**
         * xhr error.
         */
        xhrOnError: function() {
            // There was a connection error of some sort
            console.log(this.xhrSearch.statusText);
        },
        /**
         * The ajax request.
         */
        makeRequest: function() {
            const queryString = this.buildQueryString();

            this.xhrSearch = new XMLHttpRequest();
            this.xhrSearch.open('GET', this.queryUrl + queryString, true);
            this.xhrSearch.setRequestHeader('Accept', 'application/json');
            this.xhrSearch.onload = this.xhrOnLoad.bind(this);
            this.xhrSearch.onerror = this.xhrOnError.bind(this);
            this.xhrSearch.send();
        },
        /**
         * Search the static data.
         */
        searchData: function() {
            const rawTyped = this.input.value;
            if (rawTyped === '' && !this.allowEmptySearch) {
                this.closeList();
                return;
            }
            let query = (this.queryProcessor !== null) ? this.queryProcessor(rawTyped) : rawTyped;
            query = query.toLowerCase();
            if (query === '') {
                this.displayResults(this.staticData);
                return;
            }
            const results = this.staticData.filter((option) => {
                return option.label.toLowerCase().indexOf(query) !== -1;
            });
            this.displayResults(results);
        },
        /**
         * Get exact match.
         * For when submitted without choosing the dropdown.
         * Only works with when staticData is set.
         * @returns {Object|null}
         */
        getExactMatch: function() {
            const rawTyped = this.input.value.trim().toLowerCase();
            if (rawTyped === '') {
                return null;
            }
            const result = this.staticData.find((option) => {
                return option.label.toLowerCase() === rawTyped;
            });
            if (!result) {
                return null;
            }
            return result;
        },
        /**
         * Inititiate a search.
         */
        query: function() {
            if (this.staticData.length > 0) {
                this.searchData();
                return;
            }
            if (this.throttle) {
                clearTimeout(this.throttle);
            }
            if (this.xhrSearch) {
                this.xhrSearch.abort();
            }
            if (this.input.value === '' && !this.allowEmptySearch) {
                this.closeList();
                return;
            }
            this.throttle = setTimeout(this.makeRequest.bind(this), 500);
        },
        /**
         * Destroy. Remove events, etc.
         */
        disable: function() {
            this.list.parentNode.removeChild(this.list);
            // this.input reset attributes, remove listeners
        },
        /**
         * Create the list to hold the results
         */
        createList: function() {
            this.list = document.createElement('ul');
            this.list.id = this.getListId();
            this.list.setAttribute('role', 'listbox');
            this.list.setAttribute('aria-label', this.listLabel);
            this.list.classList.add('s-ui-autocomplete-list');
            this.list.classList.add('hidden');
            // insert list after input
            this.input.parentNode.insertBefore(this.list, this.input.nextSibling);
        },
        /**
         * The list id based on the input id.
         * @return {String}
         */
        getListId: function() {
            return `${this.input.id}_list`;
        },
        /**
         * The instruction id based on the input id.
         * @return {String}
         */
        getInstructionsId: function() {
            return `${this.input.id}_instructions`;
        },
        /**
         * Add appropriate attributes to the input.
         */
        formatInput: function() {
            this.input.setAttribute('role', 'combobox');
            this.input.setAttribute('aria-autocomplete', 'list');
            this.input.setAttribute('aria-expanded', 'false');
            this.input.setAttribute('aria-controls', this.getListId());
            this.input.setAttribute('aria-describedby', this.getInstructionsId());
            this.input.setAttribute('autocomplete', 'off');
            this.queryName = this.input.getAttribute('name');
        },
        /**
         * Update instructions with number of options found.
         * @param {Number} count Number of options.
         */
        updateInstructions: function(count) {
            this.instructions.innerHTML = this.instructionsText.replace('{{count}}', count);
        },
        /**
         * Handler: Key down typing in the input, handles special commands.
         * @param {Event} ev Keyup event.
         */
        handleTypingDown: function(ev) {
            const open = this.isListOpen();
            switch (ev.key) {
                case 'Escape':
                    // if list is open, close it.
                    if (open) {
                        this.closeList();
                    }
                    break;
                case 'ArrowDown':
                    // if list is open, navigate to/select first entry.
                    if (open) {
                        ev.preventDefault();
                        this.selectNext();
                    } else if (this.showAllOnFocus && this.input.value === '') {
                        this.query();
                    }
                    break;
                case 'ArrowUp':
                    // if list is open, navigate to/select first entry.
                    if (open) {
                        ev.preventDefault();
                        this.selectPrevious();
                    }
                    break;

                case 'Enter':
                    if (open && this.isOptionSelected()) {
                        ev.preventDefault();
                        this.selectOption();
                    } else if (this.preventSubmit) {
                        ev.preventDefault();
                    }
                    break;
                case 'Tab':
                    if (open && this.isOptionSelected()) {
                        this.selectOption();
                    } else if (open) {
                        this.closeList();
                    }
                    // default action
                    break;
            }
        },
        /**
         * Handler: Key up typing in the input. Does the search
         * @param {Event} ev Keyup event.
         */
        handleTypingUp: function(ev) {
            switch (ev.key) {
                case 'Escape':
                case 'ArrowDown':
                case 'ArrowUp':
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'Enter':
                case 'Tab':
                    // ignore
                    break;
                default:
                    this.typedQuery = this.input.value;
                    this.query();
                    break;
            }
        },
        /**
         * Click events on the list of results.
         * @param {Event} ev Click event.
         */
        handleClick: function(ev) {
            const li = ev.target.closest('li[aria-selected=false]');
            if (li === null) {
                return;
            }
            this.selectByClick(li);
            return;
        },
        /**
         * Clicks on the document will close the list if outside the list or input itself.
         * @param {Event} ev Click event.
         */
        handleOutsideClick: function(ev) {
            const target = ev.target;
            if (target.closest(`#${this.list.id}`) === null && target.closest(`#${this.input.id}`) === null) {
                // click target is outside autocomplete
                this.closeList();
                return;
            }
        },
        /**
         * Attach events.
         */
        attachEvents: function() {
            this.input.addEventListener('keydown', this.handleTypingDown.bind(this));
            this.input.addEventListener('keyup', this.handleTypingUp.bind(this));
            this.list.addEventListener('click', this.handleClick.bind(this));

            // Warning: This handler will not get removed if the autocomplate is removed from the page
            // Ideally we should remove it, but that would require a MutationObserver that basically watches the whole page
            // Since the parent of the autoomplete isn't always the one that is explicitly removed (and thus wouldn't trigger the observer).
            document.body.addEventListener('click', this.handleOutsideClick.bind(this));

            if (this.showAllOnFocus) {
                this.input.addEventListener('focus', () => {
                    if (this.input.value === '') {
                        this.query();
                    }
                });
            }
        },
        /**
         * Add instructions div.
         */
        addInstructions: function() {
            this.instructions = document.createElement('div');
            this.instructions.id = this.getInstructionsId();
            this.instructions.setAttribute('aria-live', 'polite');
            this.instructions.classList.add('sr-only');
            this.instructions.innerHTML = this.instructionsText.replace('{{count}}', 0);

            this.list.parentNode.insertBefore(this.instructions, this.list.nextSibling);
        },
        /**
         * Validate the config option.
         * @param {Object} config Config object.
         */
        validateConfig: function(config) {
            if (!config.inputSelector) {
                throw new Error('Must set inputSelector.');
            }
            if (this.input === null) {
                throw new Error(`${config.inputSelector} not found in the DOM.`);
            }
            if (!this.queryUrl && this.staticData.length === 0) {
                throw new Error('Must set queryUrl or staticData.');
            }
        },
        /**
         * Set config.
         * @param {Object} config Config object.
         */
        setConfig: function(config) {
            this.input = document.querySelector(config.inputSelector);

            const validConfigs = [
                'queryUrl',
                'staticData',
                'queryProcessor',
                'dataProcessor',
                'selectCallback',
                'resultLimit',
                'queryData',
                'instructionsText',
                'preventSubmit',
                'listLabel',
                'showAllOnFocus',
                'allowEmptySearch'
            ];

            const props = Object.keys(config);
            props.forEach(function(prop) {
                if (validConfigs.indexOf(prop) === -1) {
                    return;
                }
                this[prop] = config[prop];
            }, this);

            if (this.showAllOnFocus) {
                this.allowEmptySearch = true;
            }
        },
        /**
         * Initialize
         * @param {Object} config Config object.
         * @param {String} inputSelector For the input to add autocomplete to.
         * @param {String} queryUrl Url to send ajax requests to
         * @param {Array} staticData Data for autocomplete in lieu of an ajax request.
         * @param {String} instructionsText Translation of instructions for screen readers.
         * @param {Number} [resultLimit] Max results to retrieve/show. Sent with query.
         * @param {Function} [queryProcessor] Function that accepts typed query and returns it transformed for search.
         * @param {Function} [dataProcessor] Function that accepts return data array and formats it.
         * @param {Function} [selectCallback] Function for when a option is selected.
         * @param {Boolean} [queryData] Data to send with the query
         * @param {Boolean} [preventSubmit] Prevent form submit.
         * @param {Boolean} [showAllOnFocus] Show the list on first focus.
         * @param {Boolean} [allowEmptySearch] Allow empty/blank search.
         */
        init: function(config) {
            this.setConfig(config);
            this.validateConfig(config);
            this.formatInput();
            this.createList();
            this.addInstructions();
            this.attachEvents();
        }
    };

    /**
     * Initialize a new autocomplete.
     * @param {Object} config Config object passed to autocomplete.init
     * @return {autocomplete}
     */
    const initAutocomplete = function(config) {
        const a = Object.create(autocomplete);
        a.init(config);
        return a;
    };

    root.springSpace = root.springSpace || {};
    root.springSpace.sui = root.springSpace.sui || {};
    root.springSpace.sui.initAutocomplete = root.springSpace.sui.initAutocomplete || initAutocomplete;
})(this);
